import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { calculateProfileCompletion, getCompletionMessage } from '../utils/profileHelpers';

import EditableField from '../components/EditableField';
import ProfileSkeleton from '../components/ProfileSkeleton';
import Toast from './Toast';
import Badge from '../components/achievement/Badge';
import Certificate from '../components/achievement/Certificate';
import { useEnrollment, PROGRAM_SUBJECT_KEYS } from '../utils/useEnrollment';
import { useAllProgress } from '../utils/useProgress';
import { computeSubjectStats } from '../config/tutorialCatalogue';
import { earnedChapterBadges, earnedSubjectCertificates, earnedProgramTrophies, subjectMeta, resolveSubjectLabel, PROGRAM_META } from '../utils/achievements';

import SocialSidebar from '../components/SocialSideBar';
import styles from './Profile.module.css'; // Using CSS Modules for a world-class build

const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
    <circle cx="12" cy="13" r="3"></circle>
  </svg>
);

const Profile = () => {
  const { currentUser, userData, loading: authLoading, logout } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState({ percentage: 100, isComplete: true, missingFields: [] });
  const navigate = useNavigate();

  const [expandedTestIndex, setExpandedTestIndex] = useState(null);
  const [questionImages, setQuestionImages] = useState({});
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);

  const [modalImage, setModalImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const imageRef = useRef(null);

  const { enrolled } = useEnrollment();
  const allProgress = useAllProgress(enrolled);
  const chapterBadges = earnedChapterBadges(allProgress);
  const subjectCerts = earnedSubjectCertificates(allProgress);
  const programTrophies = earnedProgramTrophies(allProgress, enrolled);
  const [viewCert, setViewCert] = useState(null);
  const [viewEnrollDetails, setViewEnrollDetails] = useState(null); // programId → show its enrollment details
  // enrollmentDetails is a map: { [programId]: {...}, latest: {...} } (or legacy flat object)
  const enrollMap = userData?.enrollmentDetails || null;
  const enrollmentDetails = enrollMap
    ? (enrollMap.latest && typeof enrollMap.latest === 'object' ? enrollMap.latest
      : (enrollMap.fullName ? enrollMap : null)) // legacy flat shape
    : null;
  const allEnrollments = enrollMap
    ? Object.entries(enrollMap).filter(([k, v]) => k !== 'latest' && v && typeof v === 'object' && v.fullName)
    : [];
  // JEE/NEET share physics & chemistry — personalise certificate labels
  const preparingFor = profileData?.preparingFor || enrollmentDetails?.preparingFor || '';
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startPanX = useRef(0);
  const startPanY = useRef(0);

  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/login');
    }
    if (userData) {
      setProfileData(userData);
      // Calculate profile completion for all users
      const completion = calculateProfileCompletion(userData);
      setProfileCompletion(completion);
    }
  }, [currentUser, userData, authLoading, navigate]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleUpdate = async (field, value) => {
    if (!currentUser) return;
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, { [field]: value });
      const updatedData = { ...profileData, [field]: value };
      setProfileData(updatedData);
      
      // Recalculate profile completion for all users
      const completion = calculateProfileCompletion(updatedData);
      setProfileCompletion(completion);
      
      showToast(`${field.charAt(0).toUpperCase() + field.slice(1)} updated!`);
    } catch (error) {
      showToast(`Update failed.`, 'error');
      throw error;
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentUser) return;
    setIsUploading(true);
    try {
      const storageRef = ref(storage, `profilePhotos/${currentUser.uid}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'users', currentUser.uid), { photoURL: downloadURL });
      await updateProfile(currentUser, { photoURL: downloadURL });
      setProfileData((prev) => ({ ...prev, photoURL: downloadURL }));
      showToast('Profile photo updated!');
    } catch (error) {
      showToast('Photo upload failed.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      showToast('Logout failed.', 'error');
    }
  };

  const toggleDetails = async (originalTestIndex, testData) => {
    if (expandedTestIndex === originalTestIndex) {
      setExpandedTestIndex(null);
      return;
    }
    setActiveQuestionIndex(0);
    setAnalysisLoading(true);
    setExpandedTestIndex(originalTestIndex);
    setQuestionImages({});
    
    try {
      const folderRef = ref(storage, testData.storagePath);
      const res = await listAll(folderRef);
      const allImageUrls = await Promise.all(
        res.items.map(async (itemRef) => {
          const match = itemRef.name.match(/(\d+)/);
          const id = match ? parseInt(match[1], 10) : null;
          if (id === null) return null;
          const url = await getDownloadURL(itemRef);
          return { id, url };
        })
      );
      const imageMap = allImageUrls.filter(i => i).reduce((acc, i) => ({ ...acc, [i.id]: i.url }), {});
      setQuestionImages(imageMap);
    } catch (error) {
      showToast("Could not load analysis images.", "error");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const openImageModal = (imageUrl) => {
    setModalImage(imageUrl);
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = () => {
    setModalImage(null);
    document.body.style.overflow = '';
  };

  if (authLoading || !profileData) return <ProfileSkeleton />;

  return (
    <div className={styles.profileContainer}>
      <SocialSidebar />
      <div className={styles.dashboardLayout}>

        {/* Top Section: Hero Card */}
        <div className={styles.profileHero}>
          <div className={styles.heroGlow} aria-hidden="true" />
          <div className={styles.avatarWrapper}>
            <div className={`${styles.photoFrame} ${isUploading ? styles.uploading : ''}`}>
              <img src={profileData.photoURL || '/default-avatar.png'} alt="Profile" className={styles.profileImg} />
              <label className={styles.camLabel}>
                <CameraIcon />
                <input type="file" onChange={handlePhotoUpload} accept="image/*" disabled={isUploading} hidden />
              </label>
            </div>
            <div className={styles.heroText}>
              <h2 className={styles.userName}>{profileData.username}</h2>
              <p className={styles.userEmail}>{profileData.email}</p>
              <div className={styles.badgeRow}>
                <span className={styles.statusBadge}>{profileData.class ? `Class ${profileData.class}` : 'Student'}</span>
                {profileData.preparingFor && <span className={styles.goalBadge}>🎯 {profileData.preparingFor}</span>}
              </div>
            </div>
          </div>

          {/* Quick stats */}
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{chapterBadges.length}</span>
              <span className={styles.heroStatLabel}>Badges</span>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{subjectCerts.length}</span>
              <span className={styles.heroStatLabel}>Certificates</span>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{enrolled.length}</span>
              <span className={styles.heroStatLabel}>Courses</span>
            </div>
          </div>
        </div>

        {/* Profile Completion Banner */}
        {!profileCompletion.isComplete && (
          <div className={styles.completionBanner}>
            <div className={styles.completionHeader}>
              <div className={styles.completionInfo}>
                <h3 className={styles.completionTitle}>
                  📋 Complete Your Profile
                </h3>
                <p className={styles.completionMessage}>
                  {getCompletionMessage(profileCompletion.percentage)}
                </p>
              </div>
              <div className={styles.completionPercentage}>
                <div className={styles.circularProgress}>
                  <svg className={styles.progressRing} width="56" height="56">
                    <circle
                      className={styles.progressRingCircle}
                      stroke="#e5e7eb"
                      strokeWidth="5"
                      fill="transparent"
                      r="24"
                      cx="28"
                      cy="28"
                    />
                    <circle
                      className={styles.progressRingCircleFill}
                      stroke={profileCompletion.percentage <= 50 ? '#ef4444' : profileCompletion.percentage <= 75 ? '#f59e0b' : '#10b981'}
                      strokeWidth="5"
                      fill="transparent"
                      r="24"
                      cx="28"
                      cy="28"
                      style={{
                        strokeDasharray: `${2 * Math.PI * 24}`,
                        strokeDashoffset: `${2 * Math.PI * 24 * (profileCompletion.percentage / 100)}`,
                        transition: 'stroke-dashoffset 0.5s ease'
                      }}
                    />
                  </svg>
                  <span className={styles.percentageText}>{100 - profileCompletion.percentage}%</span>
                </div>
              </div>
            </div>
            {profileCompletion.missingFields.length > 0 && (
              <div className={styles.missingFieldsList}>
                <p className={styles.missingFieldsTitle}>Missing fields:</p>
                <ul className={styles.missingFieldsItems}>
                  {profileCompletion.missingFields.map(field => (
                    <li key={field.key}>❌ {field.label}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ── My Enrolled Courses ────────────────────────────────── */}
        {enrolled.length > 0 && (
          <div className={styles.achievementsSection}>
            <h3 className={styles.cardTitle}>📚 My Enrolled Courses</h3>
            <div className={styles.courseGrid}>
              {enrolled.map(pid => {
                const meta = PROGRAM_META[pid] || { label: pid, hue: '#64748b', hue2: '#334155', emoji: '🎓' };
                const keys = PROGRAM_SUBJECT_KEYS[pid] || [];
                let done = 0, total = 0;
                keys.forEach(k => { const s = computeSubjectStats(k, allProgress); done += s.done; total += s.total; });
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={pid} className={styles.courseCard} style={{ '--ch': meta.hue, '--ch2': meta.hue2 }}>
                    <span className={styles.courseCardEmoji} style={{ background: `linear-gradient(135deg, ${meta.hue}, ${meta.hue2})` }}>{meta.emoji}</span>
                    <span className={styles.courseCardLabel}>{meta.label}</span>
                    <span className={styles.courseCardPct}>{pct}%</span>
                    <div className={styles.courseCardBar}>
                      <div className={styles.courseCardBarFill} style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${meta.hue}, ${meta.hue2})` }} />
                    </div>
                    <span className={styles.courseCardSub}>{done}/{total} items</span>
                    <button
                      type="button"
                      className={styles.courseDetailsBtn}
                      onClick={() => setViewEnrollDetails(pid)}
                    >
                      Details
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Achievements & Enrollment ─────────────────────────── */}
        <div className={styles.achievementsSection}>
          <h3 className={styles.cardTitle}>🏅 My Achievements</h3>
          {chapterBadges.length === 0 && subjectCerts.length === 0 ? (
            <p className={styles.empty}>Complete a chapter (all lectures + DPPs + tests) to earn your first badge!</p>
          ) : (
            <>
              {subjectCerts.length > 0 && (
                <div className={styles.certGrid}>
                  {subjectCerts.map(c => (
                    <button key={c.subjectKey} type="button" className={styles.certTile}
                      style={{ '--ch': c.meta.hue, '--ch2': c.meta.hue2 }}
                      onClick={() => setViewCert(c)}>
                      <span className={styles.certTileIcon} style={{ background: `linear-gradient(135deg, ${c.meta.hue}, ${c.meta.hue2})` }}>📜</span>
                      <span className={styles.certTileLabel}>{resolveSubjectLabel(c.subjectKey, preparingFor)}</span>
                      <span className={styles.certTileSub}>Certificate</span>
                    </button>
                  ))}
                  {programTrophies.map(t => (
                    <button key={t.programId} type="button" className={styles.certTile}
                      style={{ '--ch': t.meta.hue, '--ch2': t.meta.hue2 }}
                      onClick={() => setViewCert({ meta: t.meta, badgeCount: 0, program: true })}>
                      <span className={styles.certTileIcon} style={{ background: `linear-gradient(135deg, ${t.meta.hue}, ${t.meta.hue2})` }}>🏆</span>
                      <span className={styles.certTileLabel}>{t.meta.label}</span>
                      <span className={styles.certTileSub}>Program Trophy</span>
                    </button>
                  ))}
                </div>
              )}
              <div className={styles.badgeGrid}>
                {chapterBadges.map(b => (
                  <Badge key={`${b.subjectKey}:${b.chapterId}`}
                    emoji={b.meta.emoji} hue={b.meta.hue} hue2={b.meta.hue2}
                    title={b.title} subtitle={resolveSubjectLabel(b.subjectKey, preparingFor)}
                    studentName={profileData.username} />
                ))}
              </div>
            </>
          )}
        </div>

        <div className={styles.gridContent}>
          {/* Left Column: Info */}
          <div className={styles.infoCard}>
            <h3 className={styles.cardTitle}>Academic Details</h3>
            <div className={styles.fieldsGrid}>
              <EditableField label="Mobile" value={profileData.mobile} onSave={(v) => handleUpdate('mobile', v)} inputType="tel" />
              <EditableField label="Class" value={profileData.class} onSave={(v) => handleUpdate('class', v)} inputType="select" options={[{value:'6', label:'6'},{value:'7', label:'7'},{value:'8', label:'8'},{value:'9', label:'9'},{value:'10', label:'10'},{value:'11', label:'11'},{value:'12', label:'12'},{value:'Dropper', label:'Dropper'}]} />
              <EditableField label="Preparing For" value={profileData.preparingFor} onSave={(v) => handleUpdate('preparingFor', v)} inputType="select" options={[{value:'JEE', label:'JEE'},{value:'NEET', label:'NEET'},{value:'Board Exam', label:'Board Exam'},{value:'School', label:'School'},{value:'Olympiad', label:'Olympiad'}]} />
              <EditableField label="Address" value={profileData.address} onSave={(v) => handleUpdate('address', v)} />
            </div>
            <button className={styles.logoutLink} onClick={handleLogout}>Sign Out from Portal</button>
          </div>

          {/* Right Column: History */}
          <div className={styles.historyCard}>
            <h3 className={styles.cardTitle}>Performance History</h3>
            {profileData.testsTaken?.length > 0 ? (
              <div className={styles.testTimeline}>
                {profileData.testsTaken.map((t, idx) => {
                  const testId = `test-${idx}`;
                  const isExpanded = expandedTestIndex === testId;
                  return (
                    <div key={testId} className={`${styles.testEntry} ${isExpanded ? styles.activeTest : ''}`}>
                      <div className={styles.testHeader} onClick={() => toggleDetails(testId, t)}>
                        <div className={styles.testInfoMain}>
                          <h4>{t.testName}</h4>
                          <span>{new Date(t.date).toLocaleDateString()}</span>
                        </div>
                        <div className={styles.testScoreBadge}>{t.score}/{t.total}</div>
                      </div>
                      
                      {isExpanded && (
                        <div className={styles.analysisModule}>
                          <div className={styles.statSummary}>
                            <span className={styles.correct}>Correct: {t.correct}</span>
                            <span className={styles.incorrect}>Incorrect: {t.incorrect}</span>
                            <span className={styles.attempted}>Attempted: {t.attempted}</span>
                          </div>
                          
                          {analysisLoading ? <div className={styles.loader}>Analyzing Results...</div> : (
                            <div className={styles.questionSlider}>
                              <div className={styles.sliderControls}>
                                <button onClick={() => setActiveQuestionIndex(p => Math.max(0, p-1))} disabled={activeQuestionIndex === 0}>←</button>
                                <span>Question {activeQuestionIndex + 1} of {t.questionDetails.length}</span>
                                <button onClick={() => setActiveQuestionIndex(p => Math.min(t.questionDetails.length-1, p+1))} disabled={activeQuestionIndex === t.questionDetails.length-1}>→</button>
                              </div>
                              
                              {t.questionDetails[activeQuestionIndex] && (
                                <div className={`${styles.qCard} ${styles[t.questionDetails[activeQuestionIndex].status]}`}>
                                  {questionImages[t.questionDetails[activeQuestionIndex].questionId] && (
                                    <img 
                                      src={questionImages[t.questionDetails[activeQuestionIndex].questionId]} 
                                      alt="Question" 
                                      onClick={() => openImageModal(questionImages[t.questionDetails[activeQuestionIndex].questionId])}
                                    />
                                  )}
                                  <div className={styles.qMeta}>
                                    <p>Your: <strong>{String(t.questionDetails[activeQuestionIndex].userAnswer)}</strong></p>
                                    <p>Correct: <strong>{String(t.questionDetails[activeQuestionIndex].correctAnswer)}</strong></p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : <p className={styles.empty}>No tests attempted yet.</p>}
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Certificate viewer */}
      {viewCert && (
        <div className={styles.modalOverlay} onClick={() => setViewCert(null)}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: 600, width: '100%' }}>
            <Certificate
              studentName={profileData.username}
              title={viewCert.subjectKey ? resolveSubjectLabel(viewCert.subjectKey, preparingFor) : viewCert.meta.label}
              subtitle={viewCert.program ? 'All subjects completed' : `All ${viewCert.badgeCount} chapters completed`}
              hue={viewCert.meta.hue}
              hue2={viewCert.meta.hue2}
              emoji={viewCert.meta.emoji}
            />
            <button className={styles.closeModal} onClick={() => setViewCert(null)} style={{ marginTop: 12 }}>Close</button>
          </div>
        </div>
      )}

      {/* Enrollment details modal (from a course card "Details" button) */}
      {viewEnrollDetails && (() => {
        const d = (enrollMap && enrollMap[viewEnrollDetails]) || enrollmentDetails;
        if (!d) return null;
        return (
          <div className={styles.modalOverlay} onClick={() => setViewEnrollDetails(null)}>
            <div className={styles.enrollModal} onClick={e => e.stopPropagation()}>
              <div className={styles.enrollModalHeader}>
                <div>
                  <h3 className={styles.enrollModalTitle}>Enrollment Details</h3>
                  <p className={styles.enrollModalSub}>{d.programLabel || 'Program'}</p>
                </div>
                <button type="button" className={styles.enrollModalClose} onClick={() => setViewEnrollDetails(null)} aria-label="Close">✕</button>
              </div>
              <div className={styles.enrollModalBody}>
                <div className={styles.detailRow}><span>Full Name</span><strong>{d.fullName}</strong></div>
                <div className={styles.detailRow}><span>Class</span><strong>{d.className}</strong></div>
                <div className={styles.detailRow}><span>Preparing For</span><strong>{d.preparingFor}</strong></div>
                <div className={styles.detailRow}><span>Phone</span><strong>{d.phone}</strong></div>
                <div className={styles.detailRow}><span>Date of Birth</span><strong>{d.dob}</strong></div>
                <div className={styles.detailRow}><span>Father's Name</span><strong>{d.fatherName}</strong></div>
                <div className={styles.detailRow}><span>Mother's Name</span><strong>{d.motherName}</strong></div>
                <div className={styles.detailRow}><span>Preparation Mode</span><strong>
                  {{ self: 'Self Study', offline: 'Offline Coaching', online: 'Online Coaching', prrepx: 'PrrepX Fully', other: 'Other' }[d.prepMode] || d.prepMode}
                  {d.coachingName ? ` · ${d.coachingName}` : ''}
                </strong></div>
                {d.additional && <div className={`${styles.detailRow} ${styles.detailRowFull}`}><span>Additional Notes</span><strong>{d.additional}</strong></div>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Zoom Modal preserved for functionality */}
      {modalImage && (
        <div className={styles.modalOverlay} onClick={closeImageModal}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
             <img src={modalImage} alt="Zoomed" className={styles.zoomedImg} />
             <button className={styles.closeModal} onClick={closeImageModal}>Close Analysis View</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;